import { util } from '@/api/util';
import mergeWith from 'lodash/mergeWith';
import { api as masterApi } from '@/api/api';
import { apis } from '@/config';
import { writeContactsToLoki, writeOrgsToLoki, writeLxInfoToLoki, readLxInfoFromLoki } from './contact_sync_enterprise_loki';
import { writePersonalMarksToLoki, rebuildPersonalContactsFromDexie } from './contact_sync_personal_loki';
import { savePersonalSyncStatus } from './contact_sync_personal_status';
export class ContactDBPersonal {
    constructor(ctx, base, search, mark) {
        this.ctx = ctx;
        this.base = base;
        this.search = search;
        this.mark = mark;
    }
    getLokiApi() {
        return masterApi.requireLogicalApi(apis.dbMemoryApiImpl);
    }
    async personalAllInto(options) {
        var _a, _b;
        const { list: _list, force = false, isMainAccount, _account } = options;
        const T_now = Date.now();
        const list = util.singleToList(_list);
        const { contact: personalList, contactItem: personalInfoList, orgContact: personalOrgContactList, markedList, } = this.ctx.contactTrans.transPersonalContact2MutipleData(list, T_now);
        try {
            const dbAccount = _account || this.ctx.contactUtil.getCurrentAccount();
            const lokiApi = this.getLokiApi();
            const tracker = this.ctx.tracker;
            const currentSessionId = (_b = (_a = tracker === null || tracker === void 0 ? void 0 : tracker.getCurrentSession()) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '';
            const hasData = personalList.length > 0;
            if (hasData) {
                await this.replacePersonalInLoki(lokiApi, { personalList, personalInfoList, markedList }, dbAccount, tracker, currentSessionId);
                this.syncPersonalToDexie({ personalList, personalInfoList, personalOrgContactList, markedList }, { isMainAccount, _account }, T_now, tracker, currentSessionId);
            }
            else if (force) {
                await this.replacePersonalInLoki(lokiApi, { personalList: [], personalInfoList: [], markedList: [] }, dbAccount);
                this.clearPersonalFromDexie(_account);
            }
            else {
                await this.ensurePersonalLokiLoaded(dbAccount);
            }
        }
        catch (ex) {
            console.error('[contactDB].personalAllInto.error(1):', ex);
        }
        return [
            {
                contact: {
                    deleteDiff: [],
                    updateDiff: [],
                },
                _account,
            },
        ];
    }
    async replacePersonalInLoki(lokiApi, data, dbAccount, tracker, sessionId) {
        try {
            await Promise.all([
                lokiApi.deleteByByRangeCondition({
                    dbName: 'contact_search',
                    tableName: 'contact',
                    adCondition: {
                        type: 'equals',
                        field: 'type',
                        args: ['personal'],
                    },
                    _dbAccount: dbAccount,
                }),
                lokiApi.deleteByByRangeCondition({
                    dbName: 'contact_search',
                    tableName: 'personal_mark',
                    adCondition: {
                        type: 'equals',
                        field: 'type',
                        args: [1],
                    },
                    _dbAccount: dbAccount,
                }),
            ]);
        }
        catch (lokiCleanErr) {
            console.warn('[contactDB].personalAllInto.lokiPreClean error:', lokiCleanErr);
        }
        const { personalList, personalInfoList, markedList } = data;
        if (!personalList.length && !markedList.length) {
            return;
        }
        const currentSessionId = sessionId !== null && sessionId !== void 0 ? sessionId : '';
        await Promise.all([
            writeContactsToLoki(lokiApi, personalList, personalInfoList, dbAccount)
                .then(() => {
                tracker === null || tracker === void 0 ? void 0 : tracker.updateLokiProgress(currentSessionId, 'personal', 'contact', {
                    written: personalList.length,
                    total: personalList.length,
                    status: 'done',
                });
            })
                .catch(e => {
                console.warn('[contactDB].personalAllInto.loki write error:', e);
            }),
            writePersonalMarksToLoki(lokiApi, markedList, dbAccount)
                .then(() => {
                tracker === null || tracker === void 0 ? void 0 : tracker.updateLokiProgress(currentSessionId, 'personal', 'personal_mark', {
                    written: markedList.length,
                    total: markedList.length,
                    status: 'done',
                });
            })
                .catch(e => {
                console.warn('[contactDB].personalAllInto.loki mark write error:', e);
            }),
        ]);
    }
    syncPersonalToDexie(data, options, T_now, tracker, sessionId) {
        const { personalList, personalInfoList, personalOrgContactList, markedList } = data;
        const { isMainAccount, _account } = options;
        const currentSessionId = sessionId !== null && sessionId !== void 0 ? sessionId : '';
        const makeDexieProgress = (tableName, totalCount) => ({
            onProgress: tracker
                ? () => {
                    tracker.updateDexieProgress(currentSessionId, 'personal', tableName, {
                        written: totalCount,
                        total: totalCount,
                        status: 'done',
                    });
                }
                : undefined,
        });
        Promise.all([
            this.base.intoTable({
                tableName: this.ctx.contactUtil.contactTable,
                list: personalList,
                isMainAccount,
                _account,
            }, Object.assign({ supportCache: 'enable' }, makeDexieProgress(this.ctx.contactUtil.contactTable, personalList.length))),
            this.base.intoTable({ tableName: this.ctx.contactUtil.contactItemTable, list: personalInfoList, isMainAccount, _account }, makeDexieProgress(this.ctx.contactUtil.contactItemTable, personalInfoList.length)),
            this.base.intoTable({
                tableName: this.ctx.contactUtil.orgContactTable,
                list: personalOrgContactList,
                isMainAccount,
                _account,
            }, makeDexieProgress(this.ctx.contactUtil.orgContactTable, personalOrgContactList.length)),
            this.mark.doInsertPersonalMark({
                list: markedList,
                isMainAccount,
                _account,
            }, { type: 1, isIncrease: false, _lastUpdateTime: T_now }),
        ])
            .then(() => {
            if (personalList && personalList.length) {
                return this.cleanInvalidPersonalContact({
                    lastUpdateTime: T_now,
                    _account,
                });
            }
            return undefined;
        })
            .then(() => {
            savePersonalSyncStatus(masterApi.getDataStoreApi(), { status: 'done', lastDexieWriteTime: T_now }, _account);
        })
            .catch((err) => {
            console.error('[contactDB].personalAllInto.dexie background write error:', err);
        });
    }
    clearPersonalFromDexie(_account) {
        Promise.all([
            this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.contactTable,
                adCondition: {
                    type: 'equals',
                    field: 'type',
                    args: ['personal'],
                },
                _dbAccount: _account,
            }),
            this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.contactItemTable,
                adCondition: {
                    type: 'equals',
                    field: 'type',
                    args: ['personal'],
                },
                _dbAccount: _account,
            }),
            this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.orgContactTable,
                adCondition: {
                    type: 'equals',
                    field: 'type',
                    args: ['personal'],
                },
                _dbAccount: _account,
            }),
            this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.personalMarkTable,
                adCondition: {
                    type: 'equals',
                    field: 'type',
                    args: [1],
                },
                _dbAccount: _account,
            }),
        ]).catch(err => {
            console.error('[contactDB].clearPersonalFromDexie error:', err);
        });
    }
    async clearPersonalOrgFromLoki(lokiApi, dbAccount) {
        try {
            await Promise.all([
                lokiApi.deleteByByRangeCondition({
                    dbName: 'contact_search',
                    tableName: 'org',
                    adCondition: {
                        type: 'equals',
                        field: 'type',
                        args: [2001],
                    },
                    _dbAccount: dbAccount,
                }),
                lokiApi.deleteByByRangeCondition({
                    dbName: 'contact_search',
                    tableName: 'personal_mark',
                    adCondition: {
                        type: 'equals',
                        field: 'type',
                        args: [2],
                    },
                    _dbAccount: dbAccount,
                }),
            ]);
        }
        catch (lokiCleanErr) {
            console.warn('[contactDB].personalOrgAllInto.lokiPreClean error:', lokiCleanErr);
        }
    }
    clearPersonalOrgFromDexie(_account) {
        Promise.all([
            this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.orgTable,
                adCondition: {
                    type: 'equals',
                    field: 'type',
                    args: [2001],
                },
                _dbAccount: _account,
            }),
            this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.personalMarkTable,
                adCondition: {
                    type: 'equals',
                    field: 'type',
                    args: [2],
                },
                _dbAccount: _account,
            }),
        ]).catch(err => {
            console.error('[contactDB].clearPersonalOrgFromDexie error:', err);
        });
    }
    async ensurePersonalLokiLoaded(dbAccount) {
        try {
            const lokiApi = this.getLokiApi();
            const records = await lokiApi.getByEqCondition({
                dbName: 'contact_search',
                tableName: 'contact',
                query: { type: 'personal' },
                _dbAccount: dbAccount,
            });
            if (!records || records.length === 0) {
                await rebuildPersonalContactsFromDexie({
                    lokiApi,
                    dbApi: this.ctx.dbApi,
                    contactUtil: this.ctx.contactUtil,
                    _dbAccount: dbAccount,
                });
            }
        }
        catch (err) {
            console.warn('[contactDB].ensurePersonalLokiLoaded error:', err);
        }
    }
    async ensurePersonalOrgLokiLoaded(dbAccount) {
        try {
            const lokiApi = this.getLokiApi();
            const records = await lokiApi.getByRangeCondition({
                dbName: 'contact_search',
                tableName: 'org',
                adCondition: {
                    type: 'equals',
                    field: 'type',
                    args: [2001],
                },
                _dbAccount: dbAccount,
            });
            if (!records || records.length === 0) {
                const [orgList, markList] = await Promise.all([
                    this.ctx.dbApi.getByEqCondition({
                        dbName: this.ctx.contactUtil.contactDbName,
                        tableName: this.ctx.contactUtil.orgTable,
                        query: { type: 2001 },
                        _dbAccount: dbAccount,
                    }),
                    this.ctx.dbApi.getByEqCondition({
                        dbName: this.ctx.contactUtil.contactDbName,
                        tableName: this.ctx.contactUtil.personalMarkTable,
                        query: { type: 2 },
                        _dbAccount: dbAccount,
                    }),
                ]);
                await Promise.all([writeOrgsToLoki(lokiApi, orgList, dbAccount), writePersonalMarksToLoki(lokiApi, markList, dbAccount)]);
            }
        }
        catch (err) {
            console.warn('[contactDB].ensurePersonalOrgLokiLoaded error:', err);
        }
    }
    personalInto(params, _account) {
        const list = util.singleToList(params);
        const _lastUpdateTime = Date.now();
        const personalList = this.ctx.contactTrans.transPersonalListToContact(list, _lastUpdateTime);
        const personalInfoList = this.ctx.contactTrans.transPersonalListToContactInfo(list, _lastUpdateTime);
        const personalOrgContactList = this.ctx.contactTrans.transPersonalListToOrgContact(list, _lastUpdateTime);
        return util.SyncCatchError(async () => {
            const contactIdList = personalList.map(item => item === null || item === void 0 ? void 0 : item.id);
            const [oldContactItemList, oldOrgContactList] = await Promise.all([
                this.search.getContactItemListByContactId({
                    idList: contactIdList,
                    _account,
                }),
                this.search.getOrgContactList({
                    idList: contactIdList,
                    type: 'contactId',
                    filterTeamOrg: false,
                    _account,
                }),
            ]);
            const newList = util.getKeyListByList(personalInfoList, 'id');
            const oldList = util.getKeyListByList(oldContactItemList, 'id');
            const oldOrgContactIdList = util.getKeyListByList(oldOrgContactList, 'id');
            const newOrgContactIdList = util.getKeyListByList(personalOrgContactList, 'id');
            const { deleteDiff } = util.getDiff(oldList, newList);
            const { deleteDiff: orgContactDeleteIdList } = util.getDiff(oldOrgContactIdList, newOrgContactIdList);
            await Promise.all([
                this.base.deleteData({
                    tableName: this.ctx.contactUtil.contactItemTable,
                    list: deleteDiff,
                    _account,
                }),
                this.base.deleteData({
                    tableName: this.ctx.contactUtil.orgContactTable,
                    list: orgContactDeleteIdList,
                    _account,
                }),
            ]);
            return Promise.all([
                this.base.intoTable({
                    tableName: this.ctx.contactUtil.contactTable,
                    list: personalList,
                    _account: _account,
                }, {
                    asSoon: true,
                }),
                this.base.intoTable({
                    tableName: this.ctx.contactUtil.contactItemTable,
                    list: personalInfoList,
                    _account: _account,
                }, {
                    asSoon: true,
                }),
                this.base.intoTable({
                    tableName: this.ctx.contactUtil.orgContactTable,
                    list: personalOrgContactList,
                    _account: _account,
                }, {
                    asSoon: true,
                }),
                writeContactsToLoki(this.getLokiApi(), personalList, personalInfoList, _account || this.ctx.contactUtil.getCurrentAccount()).catch(e => {
                    console.warn('[contactDB].personalInto.loki write error:', e);
                }),
            ]);
        });
    }
    async personalOrgAllInto(options) {
        const _lastUpdateTime = Date.now();
        const { list, isMainAccount, _account, force = false } = options;
        const { groupList: orgList, markedList } = this.ctx.contactTrans.transPersonalGroup2EntityOrgList(list, _lastUpdateTime);
        console.log('personalOrgAllInto orgList', orgList);
        const idList = util.getKeyListByList(orgList, 'id');
        let delIdList = [];
        try {
            const dbAccount = _account || this.ctx.contactUtil.getCurrentAccount();
            const lokiApi = this.getLokiApi();
            const hasData = orgList.length > 0;
            if (hasData) {
                await this.clearPersonalOrgFromLoki(lokiApi, dbAccount);
                await Promise.all([
                    writeOrgsToLoki(lokiApi, orgList, dbAccount),
                    writePersonalMarksToLoki(lokiApi, markedList, dbAccount),
                ]).catch(e => {
                    console.warn('[contactDB].personalOrgAllInto.loki write error:', e);
                });
                Promise.all([
                    this.base.intoTable({
                        tableName: this.ctx.contactUtil.orgTable,
                        list: orgList,
                        isMainAccount,
                        _account,
                    }),
                    this.mark.doInsertPersonalMark({
                        list: markedList,
                        isMainAccount,
                        _account,
                    }, {
                        isIncrease: false,
                        type: 2,
                    }),
                ])
                    .then(async () => {
                    delIdList = await this.clearnInvalidPersonalOrg({
                        _account,
                        lastUpdateTime: _lastUpdateTime,
                    });
                })
                    .catch((err) => {
                    console.error('[contactDB].personalOrgAllInto.dexie background write error:', err);
                });
            }
            else if (force) {
                await this.clearPersonalOrgFromLoki(lokiApi, dbAccount);
                this.clearPersonalOrgFromDexie(_account);
            }
            else {
                await this.ensurePersonalOrgLokiLoaded(dbAccount);
            }
        }
        catch (ex) {
            console.error('[contactDB].personalOrgAllInto.error(1):', ex);
        }
        const updateDiff = idList.filter(id => !delIdList.includes(id));
        return [
            {
                org: {
                    deleteDiff: delIdList,
                    updateDiff,
                },
            },
        ];
    }
    async personalContactDelete(params) {
        const { list: _list, _account } = params;
        const list = Array.isArray(_list) ? _list : [_list];
        try {
            const [itemList, orgContactList] = await Promise.all([
                this.search.getContactItemListByContactId({ idList: list, _account }),
                this.search.getOrgContactListByContactId({ idList: list, _account }),
            ]);
            const contactItemIdList = util.getKeyListByList(itemList, 'id');
            const orgContactIdList = util.getKeyListByList(orgContactList, 'id');
            await Promise.all([
                this.base.deleteData({ tableName: this.ctx.contactUtil.contactTable, list, _account }),
                this.base.deleteData({ tableName: this.ctx.contactUtil.contactItemTable, list: contactItemIdList, _account }),
                this.base.deleteData({ tableName: this.ctx.contactUtil.orgContactTable, list: orgContactIdList, _account }),
            ]);
            return {
                success: true,
                data: {
                    contact_personal: {
                        deleteDiff: list,
                    },
                    contactItem_personal: {
                        deleteDiff: contactItemIdList,
                    },
                    orgContact_personal: {
                        deleteDiff: orgContactIdList,
                    },
                    syncStatus: {
                        personal: true,
                    },
                },
            };
        }
        catch (e) {
            console.error('[contact] personalContactDelete error', e);
            return {
                success: false,
            };
        }
    }
    async doInsertOrReplacePersonal(params, sendContactNotify) {
        var _a;
        const { data, _account } = params;
        if ((data === null || data === void 0 ? void 0 : data.success) && (data === null || data === void 0 ? void 0 : data.data)) {
            const res = await this.personalInto(data.data, _account);
            if (res.success && res.data) {
                const idList = util.getKeyListByList(res.data[0], 'id');
                const emaillist = new Set();
                (_a = res.data[1]) === null || _a === void 0 ? void 0 : _a.forEach(item => {
                    if (item.contactItemType === 'EMAIL') {
                        emaillist.add(item.contactItemVal);
                    }
                });
                res.data = await this.ctx.contactTrans.transformContactModel({
                    contactList: res.data[0],
                    contactInfoList: res.data[1],
                    _account,
                });
                sendContactNotify({
                    contact_personal: {
                        updateDiff: idList,
                    },
                    syncStatus: {
                        personal: true,
                    },
                    emails: [...emaillist],
                    _account,
                });
            }
            return res;
        }
        return {
            success: false,
            error: data === null || data === void 0 ? void 0 : data.message,
        };
    }
    async cleanInvalidPersonalContact(params) {
        const { lastUpdateTime, _account } = params;
        const timestamp = lastUpdateTime - 10;
        const [localContactList, personalContactItemList] = await Promise.all([
            this.ctx.dbApi.getByEqCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.contactTable,
                query: {
                    type: 'personal',
                },
                _dbAccount: _account,
            }),
            this.search.getContactItemListByType('personal'),
        ]);
        const promises = [];
        const ids = [];
        localContactList.forEach(item => {
            if (item._lastUpdateTime <= timestamp) {
                ids.push(item.id);
            }
        });
        const delPersIdContactIdList = [];
        personalContactItemList.forEach((item) => {
            if ((item._lastUpdateTime || 0) < timestamp - 10) {
                delPersIdContactIdList.push(item.id);
            }
        });
        if (delPersIdContactIdList.length) {
            promises.push(this.base.deleteData({
                tableName: this.ctx.contactUtil.contactItemTable,
                list: delPersIdContactIdList,
                _account,
            }));
        }
        if (ids.length) {
            const contactDeleteRequest = this.ctx.dbApi.deleteById({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.contactTable,
                _dbAccount: _account,
            }, ids);
            const contactItemDeleteRequest = this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.contactItemTable,
                adCondition: {
                    type: 'anyOf',
                    field: 'contactId',
                    args: [ids],
                },
                _dbAccount: _account,
            });
            const orgContactDeleteRequest = this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.contactItemTable,
                adCondition: {
                    type: 'anyOf',
                    field: 'contactId',
                    args: [ids],
                },
                _dbAccount: _account,
            });
            const personalMarkDeleteRequest = this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.personalMarkTable,
                adCondition: {
                    type: 'anyOf',
                    field: 'id',
                    args: [ids],
                },
                _dbAccount: _account,
            });
            promises.push(contactDeleteRequest, contactItemDeleteRequest, orgContactDeleteRequest, personalMarkDeleteRequest);
        }
        if (promises.length) {
            await Promise.all(promises);
        }
        if (ids.length) {
            const dbAccount = _account || this.ctx.contactUtil.getCurrentAccount();
            const lokiApi = this.getLokiApi();
            try {
                await Promise.all([
                    lokiApi.deleteByByRangeCondition({
                        dbName: 'contact_search',
                        tableName: 'contact',
                        adCondition: {
                            type: 'anyOf',
                            field: 'id',
                            args: [ids],
                        },
                        _dbAccount: dbAccount,
                    }),
                    lokiApi.deleteByByRangeCondition({
                        dbName: 'contact_search',
                        tableName: 'personal_mark',
                        adCondition: {
                            type: 'anyOf',
                            field: 'value',
                            args: [ids],
                        },
                        _dbAccount: dbAccount,
                    }),
                ]);
            }
            catch (lokiCleanErr) {
                console.warn('[contactDB].cleanInvalidPersonalContact.lokiClean error:', lokiCleanErr);
            }
        }
    }
    async clearnInvalidPersonalOrg(params) {
        const { lastUpdateTime, _account } = params;
        const timestamp = lastUpdateTime - 10;
        const [localOrgList, localPersonalOrgMarkedList] = await Promise.all([
            this.ctx.dbApi.getByEqCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.orgTable,
                query: {
                    type: 2001,
                },
                _dbAccount: _account,
            }),
            this.mark.getPersonalMarkListByFields([2], 'type', _account),
        ]);
        const markedIds = new Set();
        const needDeleteMarkedList = [];
        const ids = [];
        localPersonalOrgMarkedList.forEach(item => {
            const _lastUpdateTime = item._lastUpdateTime || 0;
            if (_lastUpdateTime <= timestamp) {
                markedIds.add(item.id);
                needDeleteMarkedList.push(item);
            }
        });
        localOrgList.forEach(item => {
            const _lastUpdateTime = item._lastUpdateTime || 0;
            if (_lastUpdateTime <= timestamp) {
                ids.push(item.id);
                markedIds.add(item.id);
            }
        });
        const promiseList = [];
        if (ids.length) {
            const orgDeleteRequest = this.ctx.dbApi.deleteById({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.orgTable,
                _dbAccount: _account,
            }, ids);
            promiseList.push(orgDeleteRequest);
        }
        if (markedIds.size) {
            const personalMarkDeleteRequest = this.ctx.dbApi.deleteByByRangeCondition({
                dbName: this.ctx.contactUtil.contactDbName,
                tableName: this.ctx.contactUtil.personalMarkTable,
                adCondition: {
                    type: 'anyOf',
                    field: 'id',
                    args: [[...markedIds]],
                },
                _dbAccount: _account,
            });
            promiseList.push(personalMarkDeleteRequest);
        }
        await Promise.all(promiseList);
        const dbAccount = _account || this.ctx.contactUtil.getCurrentAccount();
        const lokiApi = this.getLokiApi();
        try {
            const lokiCleanups = [];
            if (ids.length) {
                lokiCleanups.push(lokiApi.deleteByByRangeCondition({
                    dbName: 'contact_search',
                    tableName: 'org',
                    adCondition: {
                        type: 'anyOf',
                        field: 'id',
                        args: [ids],
                    },
                    _dbAccount: dbAccount,
                }));
            }
            if (markedIds.size) {
                lokiCleanups.push(lokiApi.deleteByByRangeCondition({
                    dbName: 'contact_search',
                    tableName: 'personal_mark',
                    adCondition: {
                        type: 'anyOf',
                        field: 'value',
                        args: [[...markedIds]],
                    },
                    _dbAccount: dbAccount,
                }));
            }
            if (lokiCleanups.length) {
                await Promise.all(lokiCleanups);
            }
        }
        catch (lokiCleanErr) {
            console.warn('[contactDB].clearnInvalidPersonalOrg.lokiClean error:', lokiCleanErr);
        }
        this.ctx.eventApi.sendSysEvent({
            eventName: 'contactPersonalMarkNotify',
            eventStrData: 'notify',
            eventData: {
                actionType: 'delete',
                isAll: false,
                data: needDeleteMarkedList,
            },
            _account,
        });
        return ids;
    }
    async deleteTrashContactByManual() {
        var _a;
        const md5 = ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.accountMd5) || '';
        try {
            this.ctx.dbApi.deleteDB(this.ctx.contactUtil.contactDbName + '_' + md5);
        }
        catch (ex) {
            console.error('[contactDB]deleteTrashContactByManual.failed', ex);
        }
        return true;
    }
    personalOrgIntoOrg(list, _lastUpdateTime = Date.now()) {
        const now = Date.now();
        return list.map(item => {
            const { groupId: id, groupName: name, updateTime = now, marked } = item;
            return {
                id: this.transOrgIdByPersonalOrg(id),
                orgName: name,
                orgPYName: util.toPinyin(name),
                originId: id,
                type: 2001,
                visibleCode: 0,
                parent: 'personal_org',
                orgRank: 1,
                updateTime,
                marked,
                _lastUpdateTime,
            };
        });
    }
    transOrgIdByPersonalOrg(id) {
        if (id.startsWith('personal_org_')) {
            return id;
        }
        return 'personal_org_' + id;
    }
    async updateContactById(params) {
        const { list: originContactList, action = 'update', _account } = params;
        if (!originContactList.length) {
            return {
                success: true,
            };
        }
        const idList = util.getKeyListByList(originContactList, 'id');
        let list = [];
        try {
            const memoryModels = await this.search.getContactsInMemoryMode({
                fields: idList,
                type: 'contactId',
                _account,
            });
            list = memoryModels.map(m => {
                const model = this.search.transformMemoryModel2StandardModel(m);
                return model.contact;
            });
        }
        catch (e) {
            console.warn('[contact] updateContactById loki read fallback to dexie', e);
            list = await this.search.getContactList({ idList, _account });
        }
        if (!list.length) {
            return {
                success: false,
            };
        }
        const needUpdateContactList = [];
        list.forEach(item => {
            const findContact = originContactList.find(obj => obj.id === item.id);
            if (findContact) {
                let hasDiff = false;
                const newContact = mergeWith(item, findContact, (cur, src) => {
                    if (src !== cur) {
                        hasDiff = true;
                        if (Array.isArray(src)) {
                            if (action === 'update') {
                                return src;
                            }
                            if (action === 'insert') {
                                return [...new Set((cur || []).concat(src))];
                            }
                            return (cur || []).filter((id) => !src.includes(id));
                        }
                    }
                    return undefined;
                });
                if (hasDiff) {
                    needUpdateContactList.push({
                        contact: newContact,
                    });
                }
            }
            else {
                needUpdateContactList.push({
                    contact: item,
                });
            }
        });
        if (!needUpdateContactList.length) {
            return {
                success: true,
            };
        }
        return this.updateContactModel({ list: needUpdateContactList, _account });
    }
    async updateContactModel(params) {
        const { list, _account } = params;
        try {
            const contactList = [];
            let contactItemList = [];
            const personalContactId = new Set();
            const enterpriseContactId = new Set();
            list.forEach(item => {
                const { contact, contactInfo } = item;
                if (contact) {
                    contactList.push(contact);
                    if (contact.type === 'enterprise') {
                        enterpriseContactId.add(contact.id);
                    }
                    else if (contact.type === 'personal') {
                        personalContactId.add(contact.id);
                    }
                }
                if (contactInfo === null || contactInfo === void 0 ? void 0 : contactInfo.length) {
                    contactItemList = contactItemList.concat(contactInfo);
                    const { contactId, type } = contactInfo[0];
                    if (type === 'enterprise') {
                        enterpriseContactId.add(contactId);
                    }
                    else if (type === 'personal') {
                        personalContactId.add(contactId);
                    }
                }
            });
            const dbAccount = _account || this.ctx.contactUtil.getCurrentAccount();
            const lokiApi = this.getLokiApi();
            const lxInfoUpdateMap = new Map();
            contactList.forEach(contact => {
                if (contact.avatar !== undefined) {
                    const entry = lxInfoUpdateMap.get(contact.id) || {};
                    entry.avatar = contact.avatar;
                    lxInfoUpdateMap.set(contact.id, entry);
                }
            });
            contactItemList.forEach(item => {
                if (item.contactItemType === 'yunxin') {
                    const entry = lxInfoUpdateMap.get(item.contactId) || {};
                    entry.yxid = item.contactItemVal || '';
                    entry.yxToken = item.contactItemRefer || '';
                    lxInfoUpdateMap.set(item.contactId, entry);
                }
            });
            const lxInfoContactIds = [...lxInfoUpdateMap.keys()];
            const [, , existingLxInfoRows] = await Promise.all([
                writeContactsToLoki(lokiApi, contactList, contactItemList, dbAccount),
                Promise.all([
                    this.base.intoTable({
                        tableName: this.ctx.contactUtil.contactTable,
                        list: contactList,
                        _account,
                    }),
                    this.base.intoTable({
                        tableName: this.ctx.contactUtil.contactItemTable,
                        list: contactItemList,
                        _account,
                    }),
                ]).catch(e => console.error('[contact] updateContactModel dexie write error', e)),
                lxInfoContactIds.length ? readLxInfoFromLoki(lokiApi, lxInfoContactIds, dbAccount) : Promise.resolve([]),
            ]);
            if (lxInfoContactIds.length) {
                const existingMap = new Map((existingLxInfoRows || []).map(r => [r.contactId, r]));
                const mergedRows = lxInfoContactIds.map(contactId => {
                    const existing = existingMap.get(contactId);
                    const update = lxInfoUpdateMap.get(contactId);
                    return {
                        contactId,
                        avatar: update.avatar !== undefined ? update.avatar : (existing === null || existing === void 0 ? void 0 : existing.avatar) || '',
                        yxid: update.yxid !== undefined ? update.yxid : (existing === null || existing === void 0 ? void 0 : existing.yxid) || '',
                        yxToken: update.yxToken !== undefined ? update.yxToken : (existing === null || existing === void 0 ? void 0 : existing.yxToken) || '',
                    };
                });
                await Promise.all([
                    writeLxInfoToLoki(lokiApi, mergedRows, dbAccount),
                    this.base.intoTable({
                        tableName: this.ctx.contactUtil.lxInfoTable,
                        list: mergedRows,
                        _account,
                    }),
                ]).catch(e => console.error('[contact] updateContactModel lx_info write error', e));
            }
            return {
                success: true,
                data: {
                    personalIdList: [...personalContactId],
                    enterpriseIdList: [...enterpriseContactId],
                },
            };
        }
        catch (e) {
            console.error('[contact] updateContactModel error', e);
            return {
                success: false,
            };
        }
    }
}
